import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PlayIconTransparent } from '../assets/PlayIconTransparent';
import { MEDIA_CONTROLS_HEIGHT, MediaPlayerControls } from './MediaPlayerControls';
import { mediaActions, mediaSelectors } from '../redux/media-reducer';
import { white } from '../styles/colors';
import { getLinesFromTranscript, getMsFromTimestamp, getUrnFromContent, isSmallScreen, isTouchScreen, MEDIA_TYPE, parseMediaContent, SMALL_SCREEN_MEDIA_STYLE, transparentPxImageSrc } from '../utils/media-utils';
import { VideoPlayerControlsAdapter } from '../utils/VideoPlayerControlsAdapter';
import { Spinner } from './Spinner';

/**
 * HOW THE VIDEOPLAYER SIZING WORKS:
 *
 * make a container for the video which has both width and height.  The videoplayer
 *   will fit that space, including room at the bottom for its controls, and size the
 *   video itself so the aspect ratio is preserved.
 *
 */

var CONTROLS_TOP_MARGIN = 23;
var SMALL_SCREEN_CONTROLS_TOP_MARGIN = 8;
var CONTROLS_BOTTOM_MARGIN = 23;
var SMALL_SCREEN_CONTROLS_BOTTOM_MARGIN = 8;
var CONTROLS_SIDE_MARGIN = 20;
var SMALL_SCREEN_CONTROLS_SIDE_MARGIN = 0;

var VideoPlayerDiv = styled.div({
  position: 'relative',
  width: '100%',
  height: '100%'
});

var VideoPlayButtonContainer = styled.div(function (props) {
  var style = {
    position: 'absolute',
    top: 0,
    left: 'calc(50% - ' + props.videoWidth / 2 + 'px)',
    width: props.videoWidth,
    height: props.videoHeight,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7
  };
  if (props.disabled) {
    style.pointerEvents = 'none';
  }
  // the ff is separated out so that on phone touchscreens it does not
  //  flash blue over entire video when tapped, which pointer cursors make happen
  if (!isTouchScreen()) {
    style.cursor = 'pointer';
    style[':hover'] = {
      opacity: 1.0
    };
  }
  return style;
});

var VideoPlayButtonDiv = styled.div(function (props) {
  var style = {
    width: props.buttonSize,
    height: props.buttonSize,
    opacity: props.showButton ? 'inherited' : 0
  };
  if (props.showButton) {
    style.transition = 'opacity 1000ms';
  }
  return style;
});

var VideoControlsContainer = styled.div(function (props) {
  var _style;

  var style = (_style = {
    marginTop: CONTROLS_TOP_MARGIN,
    marginBottom: CONTROLS_BOTTOM_MARGIN,
    height: MEDIA_CONTROLS_HEIGHT,
    marginLeft: CONTROLS_SIDE_MARGIN,
    marginRight: CONTROLS_SIDE_MARGIN
  }, _style[SMALL_SCREEN_MEDIA_STYLE] = {
    marginTop: SMALL_SCREEN_CONTROLS_TOP_MARGIN,
    marginBottom: SMALL_SCREEN_CONTROLS_BOTTOM_MARGIN,
    marginLeft: SMALL_SCREEN_CONTROLS_SIDE_MARGIN,
    marginRight: SMALL_SCREEN_CONTROLS_SIDE_MARGIN
  }, _style);
  return style;
});

var VideoContainer = styled.div(function (props) {
  var style = {
    position: 'relative',
    width: props.videoWidth,
    height: props.videoHeight,
    marginLeft: 'auto',
    marginRight: 'auto',
    overflow: 'hidden',
    backgroundColor: '#000',
    '& video': {
      height: '100%',
      width: '100%'
    },
    '& ::cue': {
      fontFamily: props.appFont,
      fontSize: 28,
      backgroundColor: '#222',
      color: '#fff',
      outline: 'none',
      textShadow: 'none'
    }
  };
  return style;
});

//***************************************************************************************

var VideoWithControlsBase = function (_Component) {
  _inherits(VideoWithControlsBase, _Component);

  function VideoWithControlsBase(props) {
    _classCallCheck(this, VideoWithControlsBase);

    var _this = _possibleConstructorReturn(this, _Component.call(this, props));

    _this.loadFromPuddleIfRequired = function () {
      var mediaInfo = _this.getMediaInfo();
      if (mediaInfo && mediaInfo.encryptedPuddleId && !_this.props.uriFromPuddle(mediaInfo.encryptedPuddleId)) {
        _this.props.loadResourceFromPuddle(mediaInfo.encryptedPuddleId);
        _this.setState({ loadingFromPuddle: true });
      }
    };

    _this.subscribeToVideoEvents = function () {
      _this.unsubscribeFromVideoEvents();
      if (_this.props.onEnded) {
        _this.eventSubscriptions.push(_this.videoPlayer.onEnd(_this.props.onEnded));
      }
      _this.eventSubscriptions.push(_this.videoPlayer.onPlay(_this.handleVideoStarted));
      _this.eventSubscriptions.push(_this.videoPlayer.onPause(_this.handleVideoStopped));
    };

    _this.createTranscript = function () {
      if (!_this.props.transcript) {
        return;
      }
      var track = _this.videoPlayer.video.addTextTrack('subtitles', 'English', 'en');
      var lines = getLinesFromTranscript(_this.props.transcript);
      lines.forEach(function (line) {
        line.ms = getMsFromTimestamp(line.timestamp);
      });
      var sortedLines = lines.sort(function (lineA, lineB) {
        if (lineA.ms <= lineB.ms) {
          return -1;
        }
        return 1;
      });
      sortedLines.forEach(function (line, ii) {
        var start = Math.round(line.ms / 100) / 10;
        var end = null;
        var nextCue = sortedLines[ii + 1];
        if (nextCue && nextCue.ms) {
          end = Math.round(nextCue.ms / 100) / 10;
        } else {
          end = start + 20;
        }
        var cue = null;
        if (typeof VTTCue !== 'undefined') {
          // FIXME: Edge may have implemented this
          cue = new VTTCue(start, end, line.text);
        } else {
          cue = new TextTrackCue(start, end, line.text);
        }
        track.addCue(cue);
      });
    };

    _this.unsubscribeFromVideoEvents = function () {
      var subscription = _this.eventSubscriptions.pop();
      while (subscription) {
        subscription.remove();
        subscription = _this.eventSubscriptions.pop();
      }
    };

    _this.handleVideoSizeKnown = function (e) {
      _this.setState({
        videoAspect: _this.videoElem.videoHeight / _this.videoElem.videoWidth,
        videoIsPlayable: true
      });
      _this.handleResize();
      // these force a load of the first frame of the video on phones as soon as possible
      _this.videoPlayer.setTimeAsFraction(0.0001);
      setTimeout(function () {
        _this.videoPlayer.setTimeAsFraction(0);
      }, 200);
    };

    _this.handleVideoElementCreated = function (videoElem) {
      if (!videoElem) {
        return;
      }
      _this.videoElem = videoElem;
      _this.videoPlayer.setVideo(videoElem);
      videoElem.addEventListener('loadedmetadata', _this.handleVideoSizeKnown);
      window.addEventListener('resize', _this.handleResize);
      _this.createTranscript();
    };

    _this.handlePlayPauseClick = function (e) {
      if (_this.videoPlayer.isPlaying) {
        _this.videoPlayer.pause();
      } else {
        _this.videoPlayer.play();
      }
    };

    _this.handleVideoStarted = function (e) {
      _this.setState({ showButton: false });
      if (_this.props.onPlayed) {
        _this.props.onPlayed(_this.videoPlayer.duration, _this.videoPlayer.position);
      }
    };

    _this.handleVideoStopped = function (e) {
      // wait a little after the video stops to show the big play button
      //  because the video stops and starts when it seeks or rewinds
      //  while playing, and we want to avoid flashing
      setTimeout(function () {
        if (!_this.videoPlayer.isPlaying) {
          _this.setState({ showButton: true });
        }
        if (_this.props.onPaused) {
          _this.props.onPaused(_this.videoPlayer.position);
        }
      }, 300);
    };

    _this.handleResize = function () {
      var videoHeight = 0;
      var videoWidth = 0;
      if (_this.state.videoAspect !== 0) {
        var containerElem = _this.containerRef.current;
        if (containerElem) {
          var verticalMargins = CONTROLS_TOP_MARGIN + CONTROLS_BOTTOM_MARGIN;
          if (isSmallScreen()) {
            verticalMargins = SMALL_SCREEN_CONTROLS_TOP_MARGIN + SMALL_SCREEN_CONTROLS_BOTTOM_MARGIN;
          }
          var availableHeight = containerElem.clientHeight - (verticalMargins + MEDIA_CONTROLS_HEIGHT);
          videoWidth = containerElem.clientWidth;
          videoHeight = videoWidth * _this.state.videoAspect;
          if (videoHeight > availableHeight) {
            videoHeight = availableHeight;
            videoWidth = videoHeight / _this.state.videoAspect;
          }
        }
      }
      _this.setState({
        videoHeight: videoHeight,
        videoWidth: videoWidth
      });
    };

    _this.getMediaInfo = function () {
      var videoInfoArray = parseMediaContent({
        mediaInfoOrArray: _this.props.videos,
        mediaType: MEDIA_TYPE.VIDEO,
        errorHandler: _this.props.handleError,
        isWebi: _this.props.isWebi
      });
      if (videoInfoArray && videoInfoArray.length > 0) {
        return videoInfoArray[0];
      }
      return null;
    };

    _this.videoPlayer = new VideoPlayerControlsAdapter();
    _this.eventSubscriptions = [];
    _this.state = {
      loadingFromPuddle: false,
      videoAspect: props.aspectRatio || 0,
      videoHeight: 0,
      videoWidth: 0,
      videoIsPlayable: false,
      showButton: true
    };
    _this.subscribeToVideoEvents();
    _this.containerRef = React.createRef();
    _this.videoElem = null;
    return _this;
  }

  VideoWithControlsBase.prototype.componentDidMount = function componentDidMount() {
    this.loadFromPuddleIfRequired();
    this.handleResize();
  };

  // if the videos prop changes, reload if necessary


  VideoWithControlsBase.prototype.componentDidUpdate = function componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevState.loadingFromPuddle) {
      var mediaInfo = this.getMediaInfo();
      if (mediaInfo && mediaInfo.encryptedPuddleId && this.props.uriFromPuddle(mediaInfo.encryptedPuddleId)) {
        this.setState({ loadingFromPuddle: false });
      }
      return;
    }
    var currentVideoUrn = getUrnFromContent({
      mediaInfoOrArray: this.props.videos,
      mediaType: MEDIA_TYPE.VIDEO,
      errorHandler: this.props.handleError
    });
    var previousVideoUrn = getUrnFromContent({
      mediaInfoOrArray: prevProps.videos,
      mediaType: MEDIA_TYPE.VIDEO,
      errorHandler: this.props.handleError
    });
    if (previousVideoUrn !== currentVideoUrn) {
      this.loadFromPuddleIfRequired();
    }
  };

  VideoWithControlsBase.prototype.componentWillUnmount = function componentWillUnmount() {
    this.videoPlayer.clearMedia();
    this.unsubscribeFromVideoEvents();
    window.removeEventListener('resize', this.handleResize);
    if (this.videoElem) {
      this.videoElem.removeEventListener('loadedmetadata', this.handleVideoSizeKnown);
    }
  };

  VideoWithControlsBase.prototype.render = function render() {
    if (!this.props.videos) {
      return null;
    }
    var videoUri = transparentPxImageSrc;
    if (!this.state.loadingFromPuddle) {
      var mediaInfo = this.getMediaInfo();
      if (mediaInfo && mediaInfo.encryptedPuddleId && this.props.uriFromPuddle(mediaInfo.encryptedPuddleId)) {
        videoUri = this.props.uriFromPuddle(mediaInfo.encryptedPuddleId);
      } else {
        videoUri = mediaInfo.mediaUri;
      }
    }

    // this is part of a fix for iOS:  iOS will not play video at all without a user
    //  gesture, unless (as of iOS 10) the video is muted; so this starts out muted
    //  until the initial seek is done in handleVideoSizeKnown()
    var mutedForInitialDraw = !this.state.videoIsPlayable;

    var bigSize = Math.max(this.state.videoHeight, this.state.videoWidth);
    var buttonSize = bigSize * 0.12;
    var isDisabled = this.props.disabled || this.props.isRecording || !this.state.videoIsPlayable;
    var bigButton = void 0;
    if (isDisabled) {
      bigButton = React.createElement(Spinner, null);
    } else {
      bigButton = React.createElement(PlayIconTransparent, { circleColor: white });
    }
    return React.createElement(
      VideoPlayerDiv,
      { ref: this.containerRef },
      React.createElement(
        VideoContainer,
        {
          videoHeight: this.state.videoHeight,
          videoWidth: this.state.videoWidth,
          appFont: this.props.appFont
        },
        React.createElement(
          'video',
          { ref: this.handleVideoElementCreated, playsInline: true, muted: mutedForInitialDraw, preload: 'metadata' },
          React.createElement('source', { src: videoUri, type: 'video/mp4' })
        )
      ),
      React.createElement(
        VideoPlayButtonContainer,
        {
          'data-qa': 'PlayButton',
          videoHeight: this.state.videoHeight,
          videoWidth: this.state.videoWidth,
          disabled: isDisabled,
          onClick: this.handlePlayPauseClick
        },
        React.createElement(
          VideoPlayButtonDiv,
          { buttonSize: buttonSize, showButton: this.state.showButton },
          bigButton
        )
      ),
      React.createElement(
        VideoControlsContainer,
        null,
        this.state.videoHeight > 0 && React.createElement(MediaPlayerControls, { mediaPlayer: this.videoPlayer, disabled: isDisabled })
      )
    );
  };

  return VideoWithControlsBase;
}(Component);

VideoWithControlsBase.propTypes = {
  onPlayed: PropTypes.func,
  onPaused: PropTypes.func,
  onEnded: PropTypes.func,
  videos: PropTypes.array,
  // if aspectRatio is provided, then the placeholder is shown immediately
  //  aspectRatio here means height / width
  aspectRatio: PropTypes.number,
  transcript: PropTypes.string
};

var mapStateToProps = function mapStateToProps(state) {
  return {
    uriFromPuddle: function uriFromPuddle(puddleResourceId) {
      return mediaSelectors.getLoadedResource(state, puddleResourceId);
    },
    isRecording: mediaSelectors.isRecording(state),
    isWebi: mediaSelectors.isWebi(state),
    appFont: mediaSelectors.contentFont(state)
  };
};

var mappedActions = {
  loadResourceFromPuddle: mediaActions.loadResourceFromPuddle,
  handleError: mediaActions.handleError
};

export var VideoWithControls = connect(mapStateToProps, mappedActions)(VideoWithControlsBase);